import { bubbleCircle, type BubbleSpec } from './bubble.js'
import type { CaptureMode } from './profile.js'

/**
 * One frame of a capture: the screen, and the camera in a circle over it —
 * or the camera alone, filling the frame. Written once for two callers:
 * the worker, painting VideoFrames onto an OffscreenCanvas, and the
 * main-thread fallback, painting <video> elements onto a canvas. The
 * sizes come with the sources because a VideoFrame and a <video> say them
 * differently.
 *
 * Painting is the cost of recording, so as little of it as possible is
 * done: `planRepaint` says whether a tick paints the whole frame (a new
 * screen frame, a moved bubble), only the bubble's box (a new camera frame
 * over a still screen), nothing at all, or writes the last frame again so
 * the recording never goes a second without one. The bubble's soft shadow,
 * the most expensive stroke here, is drawn once per size and reused.
 */

export interface PaintSource {
  image: CanvasImageSource
  width: number
  height: number
}

export type PaintContext = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D

export interface PaintInput {
  mode: CaptureMode
  screen: PaintSource | null
  camera: PaintSource | null
  bubble: BubbleSpec
}

/** Which part of the frame a paint touches. */
export type PaintRegion = 'full' | 'bubble'

/** What one tick of the compositor does. */
export type Repaint = PaintRegion | 'keepalive' | 'skip'

export interface RepaintInput {
  mode: CaptureMode
  screenChanged: boolean
  cameraChanged: boolean
  bubbleChanged: boolean
  bubbleVisible: boolean
  /** Since the last frame was written to the track. */
  sinceWriteMs: number
  /** Write a frame at least this often, still screen or not. */
  keepaliveMs: number
}

/**
 * A new screen frame or a moved bubble repaints everything; a new camera
 * frame repaints the bubble's box, or the whole frame when the camera IS
 * the frame; nothing new means nothing painted — but a frame is written
 * again once a second so a still screen still records as time passing.
 */
export function planRepaint(i: RepaintInput): Repaint {
  if (i.screenChanged || i.bubbleChanged) return 'full'
  if (i.cameraChanged && i.mode === 'camera') return 'full'
  if (i.cameraChanged && i.bubbleVisible) return 'bubble'
  return i.sinceWriteMs >= i.keepaliveMs ? 'keepalive' : 'skip'
}

/**
 * Nothing to draw over the source — no bubble to show, or the camera alone
 * — means the source frame can go out as it is, unpainted: no canvas, no
 * cost, and the encoder sets the pace.
 */
export function shouldPassThrough(
  mode: CaptureMode,
  bubbleVisible: boolean,
  hasCamera: boolean,
): boolean {
  if (mode === 'camera') return true
  return !(bubbleVisible && hasCamera)
}

/** The box the bubble and its shadow occupy, in a frame of `w` × `h`. */
export function bubbleRegion(
  spec: BubbleSpec,
  w: number,
  h: number,
): { x: number; y: number; w: number; h: number } {
  const { cx, cy, r } = bubbleCircle(spec, w, h)
  const margin = shadowMargin(r)
  const x0 = Math.max(0, Math.floor(cx - r - margin))
  const y0 = Math.max(0, Math.floor(cy - r - margin))
  const x1 = Math.min(w, Math.ceil(cx + r + margin))
  const y1 = Math.min(h, Math.ceil(cy + r + margin * 1.5))
  return { x: x0, y: y0, w: Math.max(0, x1 - x0), h: Math.max(0, y1 - y0) }
}

export function paintCapture(
  ctx: PaintContext,
  w: number,
  h: number,
  input: PaintInput,
  region: PaintRegion = 'full',
): void {
  if (region === 'bubble') {
    if (input.mode !== 'screen' || !input.bubble.visible || !input.camera) return
    const box = bubbleRegion(input.bubble, w, h)
    if (box.w <= 0 || box.h <= 0) return
    // Clipped, the screen is resampled only under the bubble's box.
    ctx.save()
    ctx.beginPath()
    ctx.rect(box.x, box.y, box.w, box.h)
    ctx.clip()
    paintAll(ctx, w, h, input)
    ctx.restore()
    return
  }
  paintAll(ctx, w, h, input)
}

function paintAll(ctx: PaintContext, w: number, h: number, input: PaintInput): void {
  ctx.fillStyle = '#0f172a'
  ctx.fillRect(0, 0, w, h)

  if (input.mode === 'camera') {
    if (input.camera) drawCover(ctx, input.camera, 0, 0, w, h)
    return
  }

  if (input.screen) drawContain(ctx, input.screen, 0, 0, w, h)
  if (input.bubble.visible && input.camera) {
    drawBubble(ctx, input.camera, bubbleCircle(input.bubble, w, h))
  }
}

// ------------------------------------------------------------------ shadow

const SHADOW_ALPHA = 0.45
const shadowBlur = (r: number): number => r * 0.35
const shadowOffsetY = (r: number): number => r * 0.12
const shadowMargin = (r: number): number => shadowBlur(r) * 2 + shadowOffsetY(r) + 2

interface ShadowSprite {
  image: CanvasImageSource
  size: number
}

/** One sprite per bubble size; a blur is a hundred times the cost of a blit. */
const shadows = new Map<number, ShadowSprite>()

function makeCanvas(w: number, h: number): OffscreenCanvas | HTMLCanvasElement | null {
  if (typeof OffscreenCanvas !== 'undefined') return new OffscreenCanvas(w, h)
  if (typeof document !== 'undefined') {
    const c = document.createElement('canvas')
    c.width = w
    c.height = h
    return c
  }
  return null
}

function shadowSprite(r: number): ShadowSprite | null {
  const key = Math.round(r)
  const cached = shadows.get(key)
  if (cached) return cached
  const size = Math.ceil(2 * (r + shadowMargin(r)))
  const canvas = makeCanvas(size, size)
  const sctx = canvas?.getContext('2d') as PaintContext | null | undefined
  if (!canvas || !sctx) return null
  sctx.shadowColor = `rgba(0,0,0,${SHADOW_ALPHA})`
  sctx.shadowBlur = shadowBlur(r)
  sctx.shadowOffsetY = shadowOffsetY(r)
  sctx.fillStyle = '#0f172a'
  sctx.beginPath()
  sctx.arc(size / 2, size / 2, r, 0, Math.PI * 2)
  sctx.fill()
  const sprite = { image: canvas, size }
  if (shadows.size >= 8) shadows.delete(shadows.keys().next().value as number)
  shadows.set(key, sprite)
  return sprite
}

/** The camera in a circle: the cached shadow under it, the picture cover-fit inside, a thin light ring. */
function drawBubble(
  ctx: PaintContext,
  camera: PaintSource,
  { cx, cy, r }: { cx: number; cy: number; r: number },
): void {
  if (r < 2) return
  const shadow = shadowSprite(r)
  if (shadow) {
    ctx.drawImage(
      shadow.image,
      cx - shadow.size / 2,
      cy - shadow.size / 2,
      shadow.size,
      shadow.size,
    )
  }

  ctx.save()
  ctx.beginPath()
  ctx.arc(cx, cy, r, 0, Math.PI * 2)
  ctx.clip()
  drawCover(ctx, camera, cx - r, cy - r, r * 2, r * 2)
  ctx.restore()

  ctx.save()
  ctx.lineWidth = Math.max(2, r * 0.04)
  ctx.strokeStyle = 'rgba(255,255,255,0.85)'
  ctx.beginPath()
  ctx.arc(cx, cy, r - ctx.lineWidth / 2, 0, Math.PI * 2)
  ctx.stroke()
  ctx.restore()
}

/** Fill the box, cropping — object-fit: cover. */
function drawCover(
  ctx: PaintContext,
  src: PaintSource,
  x: number,
  y: number,
  w: number,
  h: number,
): void {
  if (src.width <= 0 || src.height <= 0) return
  const scale = Math.max(w / src.width, h / src.height)
  const dw = src.width * scale
  const dh = src.height * scale
  ctx.drawImage(src.image, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh)
}

/** Fit inside the box, letterboxing — object-fit: contain. */
function drawContain(
  ctx: PaintContext,
  src: PaintSource,
  x: number,
  y: number,
  w: number,
  h: number,
): void {
  if (src.width <= 0 || src.height <= 0) return
  const scale = Math.min(w / src.width, h / src.height)
  const dw = src.width * scale
  const dh = src.height * scale
  ctx.drawImage(src.image, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh)
}
